import { useEffect, useId, useRef, useState } from "react";
import { primaryLeadStages } from "../config/options.js";

export function Topbar({ title, subtitle, children }) {
  return (
    <div className="topbar">
      <div>
        <h1>{title}</h1>
        <p className="subtle">{subtitle}</p>
      </div>
      <div className="topbar-actions">{children}</div>
    </div>
  );
}

export function MetricGrid({ report }) {
  return (
    <section className="metric-grid">
      <Metric label="Total Leads" value={report?.totalLeads ?? 0} />
      <Metric label="Calls" value={report?.calls ?? 0} />
      <Metric label="Visits" value={report?.visits ?? 0} />
      <Metric label="Conversions" value={report?.converted ?? 0} />
    </section>
  );
}

export function Metric({ label, value }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function Panel({ title, subtitle, children }) {
  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h2>{title}</h2>
          {subtitle ? <p className="subtle">{subtitle}</p> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

export function StageChart({ stageCounts, stages = primaryLeadStages }) {
  const visibleStages = [
    ...stages,
    ...Object.keys(stageCounts || {}).filter((stage) => !stages.includes(stage) && Number(stageCounts[stage] || 0) > 0),
  ];
  const max = Math.max(...visibleStages.map((stage) => stageCounts[stage] ?? 0), 1);
  return (
    <div className="bar-chart">
      {visibleStages.map((stage) => {
        const count = stageCounts[stage] ?? 0;
        return (
          <div className="bar-row" key={stage}>
            <span>{stage}</span>
            <div className="bar-track">
              <div className="bar" style={{ width: `${(count / max) * 100}%` }} />
            </div>
            <strong>{count}</strong>
          </div>
        );
      })}
    </div>
  );
}

export function Modal({ children, label = "Form dialog", onBack, backLabel = "Back to previous form" }) {
  const modalRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    modalRef.current?.focus({ preventScroll: true });
    return () => {
      if (previousFocus && typeof previousFocus.focus === "function") {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, []);

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={modalRef}>
        {onBack ? (
          <button className="form-back-btn" type="button" aria-label={backLabel} onClick={onBack}>
            <span aria-hidden="true">{"<"}</span>
            {backLabel}
          </button>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  maxLength = 180,
  min,
  max,
  step,
  pattern,
  suggestions = [],
  actionLabel = "",
  actionDisabled = false,
  onAction,
}) {
  const id = useId();
  const listId = useId();
  const input = (
    <input
      id={id}
      type={type}
      value={value ?? ""}
      required={required}
      maxLength={type === "number" ? undefined : maxLength}
      min={min}
      max={max}
      step={step}
      pattern={pattern}
      list={suggestions.length ? listId : undefined}
      inputMode={type === "number" ? "decimal" : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {onAction ? (
        <div className="field-control-row">
          {input}
          <FieldActionButton label={actionLabel} disabled={actionDisabled} onClick={onAction} />
        </div>
      ) : input}
      {suggestions.length ? (
        <datalist id={listId}>
          {[...new Set(suggestions.filter(Boolean))].map((suggestion) => <option key={suggestion} value={suggestion} />)}
        </datalist>
      ) : null}
    </div>
  );
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  actionLabel = "",
  actionDisabled = false,
  onAction,
  secondaryActionLabel = "",
  secondaryActionDisabled = false,
  onSecondaryAction,
}) {
  const id = useId();
  const actions = [
    onAction ? { label: actionLabel, disabled: actionDisabled, onClick: onAction, symbol: "+" } : null,
    onSecondaryAction ? { label: secondaryActionLabel, disabled: secondaryActionDisabled, onClick: onSecondaryAction, symbol: ">" } : null,
  ].filter(Boolean);
  const select = (
    <select id={id} value={value ?? ""} disabled={disabled} required={required} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => {
        const normalized = typeof option === "string" ? { value: option, label: option } : option;
        return (
          <option key={normalized.value} value={normalized.value}>
            {normalized.label}
          </option>
        );
      })}
    </select>
  );
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {actions.length ? (
        <div className="field-control-row">
          {select}
          {actions.map((action) => (
            <FieldActionButton key={action.label || action.symbol} {...action} />
          ))}
        </div>
      ) : select}
    </div>
  );
}

function FieldActionButton({ label, disabled = false, onClick, symbol = "+" }) {
  return (
    <button className="field-action-btn" type="button" aria-label={label || "Add option"} title={label || "Add option"} disabled={disabled} onClick={onClick}>
      <span aria-hidden="true">{symbol}</span>
    </button>
  );
}

export function CheckboxField({ label, checked, onChange }) {
  return (
    <label className="check-field">
      <input type="checkbox" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function AsyncActionButton({
  children,
  onAction,
  className = "mini-btn",
  pendingLabel = "Working...",
  errorMessage = "We could not complete this action. Please try again.",
  disabled = false,
  title,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState("");
  const submittingRef = useRef(false);
  const blocked = disabled || submitting;

  async function handleClick() {
    if (blocked || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setActionError("");
    try {
      const result = await onAction?.();
      if (result === false) throw new Error("Action failed");
    } catch {
      setActionError(errorMessage);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <span className="async-action">
      <button className={className} type="button" disabled={blocked} title={title} onClick={handleClick}>
        {submitting ? pendingLabel : children}
      </button>
      {actionError ? <span className="action-error">{actionError}</span> : null}
    </span>
  );
}

export function SimpleForm({ children, onSubmit, disabled = false, submitLabel = "Save", submittingLabel = "Saving..." }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submittingRef = useRef(false);
  const blocked = disabled || submitting;

  return (
    <form
      className="form-grid"
      onSubmit={async (event) => {
        event.preventDefault();
        if (disabled || submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        setSubmitError("");
        try {
          const result = await onSubmit();
          if (result === false) throw new Error("Save failed");
        } catch (error) {
          setSubmitError(formSubmitErrorMessage(error));
        } finally {
          submittingRef.current = false;
          setSubmitting(false);
        }
      }}
    >
      {children}
      {submitError ? <div className="notice full">{submitError}</div> : null}
      <div className="modal-actions full">
        <button className="primary-btn" type="submit" disabled={blocked}>
          {submitting ? submittingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
}

function formSubmitErrorMessage(error) {
  if (error?.name === "ApiError" && error.message) return error.message;
  return "We could not save this right now. Please review the details and try again.";
}

export function ResourceTable({ rows, columns, action }) {
  if (!rows?.length) return <div className="empty">No records yet.</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map(([, label]) => (
              <th key={label}>{label}</th>
            ))}
            {action ? <th>Action</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map(([key, label, formatter]) => (
                <td key={`${row.id}-${label}`}>{formatter ? formatter(row[key], row) : String(row[key] ?? "-")}</td>
              ))}
              {action ? <td>{action(row)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ResourceKeyValue({ data, formatter }) {
  const entries = Object.entries(data);
  if (!entries.length) return <div className="empty">No records yet.</div>;
  return (
    <div className="list">
      {entries.map(([key, value]) => (
        <div className="list-item" key={key}>
          <strong>{key}</strong>
          <span>{formatter ? formatter(value, key) : value}</span>
        </div>
      ))}
    </div>
  );
}
